// react
import React from 'react';

// material ui
import { Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';

export interface DeviceTabItem {
  label: string;
  /** Optional icon displayed alongside the label */
  icon?: React.ReactElement;
  /** Where the icon is placed relative to the label. Defaults to 'end'. */
  iconPosition?: 'start' | 'end' | 'top' | 'bottom';
  disabled?: boolean;
  /** Hides the tab entirely from the DOM when false (use for conditional tabs) */
  visible?: boolean;
  testId?: string;
}

interface DeviceTabsProps {
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  tabs: DeviceTabItem[];
}

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    slotProps={{
      indicator: { children: <span className="MuiTabs-indicatorSpan" /> },
    }}
  />
))(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    bottom: 1,
  },
  '& .MuiTabs-indicatorSpan': {
    // maxWidth: 75,
    height: 4,
    width: '100%',
    borderRadius: 5,
    backgroundColor: theme.palette.remoni[800],
  },
}));

const StyledTab = styled((props: React.ComponentPropsWithRef<typeof Tab>) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  minHeight: 48,
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
    opacity: 1,
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.focus,
  },
  '&.Mui-disabled': {
    opacity: 0.4,
  },
}));

const DeviceTabs = ({ activeTab, onTabChange, tabs }: DeviceTabsProps) => {
  return (
    <StyledTabs value={activeTab || 0} onChange={onTabChange}>
      {tabs
        .filter((tab) => tab.visible !== false)
        .map((tab, index) => (
          <StyledTab
            key={index}
            label={tab.label}
            icon={tab.icon}
            iconPosition={tab.icon ? (tab.iconPosition ?? 'start') : undefined}
            disabled={tab.disabled}
            data-testid={tab.testId}
          />
        ))}
    </StyledTabs>
  );
};

export default DeviceTabs;
